import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Archive, RefreshCw, Eye, RotateCcw, Trash2, FileJson, Download } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface ArchiveFile {
  name: string;
  created_at: string;
  metadata: {
    size: number;
    mimetype: string;
  };
}

interface ArchiveContent {
  archived_at: string;
  retention_days: number;
  cutoff_date: string;
  total_logs: number;
  logs: any[];
}

export function LogArchiveViewer() {
  const [archives, setArchives] = useState<ArchiveFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedArchive, setSelectedArchive] = useState<ArchiveContent | null>(null);
  const [loadingArchive, setLoadingArchive] = useState<string | null>(null);
  const [restoringArchive, setRestoringArchive] = useState<string | null>(null);
  const [deletingArchive, setDeletingArchive] = useState<string | null>(null);

  const fetchArchives = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const { data, error } = await supabase.storage
        .from('backups')
        .list('logs', {
          sortBy: { column: 'created_at', order: 'desc' },
        });

      if (error) throw error;

      const archiveFiles = (data || [])
        .filter(file => file.name.endsWith('.json'))
        .map(file => ({
          name: `logs/${file.name}`,
          created_at: file.created_at,
          metadata: {
            size: file.metadata?.size || 0,
            mimetype: file.metadata?.mimetype || 'application/json',
          },
        }));

      setArchives(archiveFiles);
    } catch (error) {
      console.error('Error fetching archives:', error);
      toast.error('Failed to fetch archives');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchArchives();
  }, []);

  const viewArchive = async (archivePath: string) => {
    setLoadingArchive(archivePath);
    try {
      const { data, error } = await supabase.storage
        .from('backups')
        .download(archivePath);

      if (error) throw error;

      const text = await data.text();
      const content: ArchiveContent = JSON.parse(text);
      setSelectedArchive(content);
    } catch (error) {
      console.error('Error loading archive:', error);
      toast.error('Failed to load archive');
    } finally {
      setLoadingArchive(null);
    }
  };

  const downloadArchive = async (archivePath: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('backups')
        .download(archivePath);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = archivePath.split('/').pop() || 'archive.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Archive downloaded');
    } catch (error) {
      console.error('Error downloading archive:', error);
      toast.error('Failed to download archive');
    }
  };

  const restoreArchive = async (archivePath: string) => {
    setRestoringArchive(archivePath);
    try {
      // Download the archive
      const { data, error } = await supabase.storage
        .from('backups')
        .download(archivePath);

      if (error) throw error;

      const text = await data.text();
      const content: ArchiveContent = JSON.parse(text);

      if (!content.logs || content.logs.length === 0) {
        toast.error('Archive contains no logs to restore');
        return;
      }

      // Insert logs back into the database in batches
      const batchSize = 100;
      let restored = 0;

      for (let i = 0; i < content.logs.length; i += batchSize) {
        const batch = content.logs.slice(i, i + batchSize).map(log => ({
          ...log,
          id: undefined, // Let the database generate new IDs
        }));

        const { error: insertError } = await supabase
          .from('admin_logs')
          .insert(batch);

        if (insertError) {
          console.error('Error restoring batch:', insertError);
          throw insertError;
        }

        restored += batch.length;
      }

      // Log the restore action
      await supabase.from('admin_logs').insert([{
        action: 'log_restore',
        entity_type: 'system',
        details: {
          archive_path: archivePath,
          restored_count: restored,
          original_archive_date: content.archived_at,
        },
      }]);

      toast.success(`Restored ${restored} logs from archive`);
      
      // Optionally delete the archive after restore
      // await deleteArchive(archivePath, false);

    } catch (error) {
      console.error('Error restoring archive:', error);
      toast.error('Failed to restore archive');
    } finally {
      setRestoringArchive(null);
    }
  };

  const deleteArchive = async (archivePath: string, showToast = true) => {
    setDeletingArchive(archivePath);
    try {
      const { error } = await supabase.storage
        .from('backups')
        .remove([archivePath]);

      if (error) throw error;

      setArchives(prev => prev.filter(a => a.name !== archivePath));
      
      if (showToast) {
        toast.success('Archive deleted');
      }
    } catch (error) {
      console.error('Error deleting archive:', error);
      if (showToast) {
        toast.error('Failed to delete archive');
      }
    } finally {
      setDeletingArchive(null);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const extractArchiveInfo = (fileName: string) => {
    // Format: logs/archive_YYYY-MM-DD_timestamp.json
    const match = fileName.match(/archive_(\d{4}-\d{2}-\d{2})_(\d+)\.json/);
    if (match) {
      return {
        date: match[1],
        timestamp: parseInt(match[2]),
      };
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Archived Logs</h2>
          <p className="text-sm text-muted-foreground">
            Browse and restore logs from the backup storage
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchArchives(true)}
          disabled={refreshing}
        >
          {refreshing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          <span className="ml-2">Refresh</span>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Archive className="h-5 w-5" />
            Archive Files
          </CardTitle>
          <CardDescription>
            {archives.length} archive{archives.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : archives.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Archive className="h-12 w-12 mb-4 opacity-50" />
              <p>No archived logs found</p>
              <p className="text-sm">Archives are created when the cleanup job runs</p>
            </div>
          ) : (
            <ScrollArea className="h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Archive Date</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {archives.map((archive) => {
                    const info = extractArchiveInfo(archive.name);
                    return (
                      <TableRow key={archive.name}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <FileJson className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">
                              {info?.date || archive.name.split('/').pop()}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {archive.created_at ? format(new Date(archive.created_at), 'MMM d, yyyy HH:mm') : 'Unknown'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {formatFileSize(archive.metadata.size)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => viewArchive(archive.name)}
                                  disabled={loadingArchive === archive.name}
                                >
                                  {loadingArchive === archive.name ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Eye className="h-4 w-4" />
                                  )}
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-4xl max-h-[80vh]">
                                <DialogHeader>
                                  <DialogTitle>Archive Contents</DialogTitle>
                                  <DialogDescription>
                                    {selectedArchive && (
                                      <span>
                                        Archived on {format(new Date(selectedArchive.archived_at), 'MMM d, yyyy HH:mm')} • 
                                        {' '}{selectedArchive.total_logs} logs • 
                                        {' '}Retention: {selectedArchive.retention_days} days
                                      </span>
                                    )}
                                  </DialogDescription>
                                </DialogHeader>
                                {selectedArchive && (
                                  <ScrollArea className="h-[500px] mt-4">
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead>Timestamp</TableHead>
                                          <TableHead>Action</TableHead>
                                          <TableHead>Entity</TableHead>
                                          <TableHead>Details</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {selectedArchive.logs.slice(0, 100).map((log, idx) => (
                                          <TableRow key={idx}>
                                            <TableCell className="font-mono text-xs">
                                              {format(new Date(log.created_at), 'MMM d, HH:mm')}
                                            </TableCell>
                                            <TableCell>
                                              <Badge variant="outline">{log.action}</Badge>
                                            </TableCell>
                                            <TableCell>{log.entity_type}</TableCell>
                                            <TableCell className="max-w-[200px] truncate">
                                              {log.details ? JSON.stringify(log.details).substring(0, 50) : '-'}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                        {selectedArchive.logs.length > 100 && (
                                          <TableRow>
                                            <TableCell colSpan={4} className="text-center text-muted-foreground">
                                              ... and {selectedArchive.logs.length - 100} more logs
                                            </TableCell>
                                          </TableRow>
                                        )}
                                      </TableBody>
                                    </Table>
                                  </ScrollArea>
                                )}
                              </DialogContent>
                            </Dialog>

                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => downloadArchive(archive.name)}
                            >
                              <Download className="h-4 w-4" />
                            </Button>

                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled={restoringArchive === archive.name}
                                >
                                  {restoringArchive === archive.name ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <RotateCcw className="h-4 w-4" />
                                  )}
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Restore Archive?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will restore all logs from this archive back to the database.
                                    This action may create duplicate entries if logs still exist.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => restoreArchive(archive.name)}>
                                    Restore
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>

                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive hover:text-destructive"
                                  disabled={deletingArchive === archive.name}
                                >
                                  {deletingArchive === archive.name ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4" />
                                  )}
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Archive?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will permanently delete this archive. This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction 
                                    onClick={() => deleteArchive(archive.name)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
